import { fabric } from 'fabric';
import { isPhotozoneImage } from '../../../../features/card-editor/utils/is-photozone-image';
import { ensureImageFillsPhotoZone, getComputedRotation } from '../../rotate-drawer/rotate-drawer-utils';

type ApplyRotateStepOptions = {
  canvas?: fabric.Canvas | null;
  activeObject?: fabric.Object | null;
  rotateRight: boolean;
  isFrontFace: boolean;
  rotateSelectedPhoto: (angle: number) => void;
};

const FRONT_FACE_ROTATE_INCREMENT = 90;
const INSIDE_FACE_ROTATE_INCREMENT = 45;

/**
 * Applies a rotation step (left or right) to the current selection. When the
 * object lives in a photozone, the image is recentered and resized so it
 * continues to fill the zone after rotation.
 *
 * Extracted from the inline desktop tool body so the rotation behavior can
 * be unit-tested independently.
 */
export const applyRotateStep = ({
  canvas,
  activeObject,
  rotateRight,
  isFrontFace,
  rotateSelectedPhoto,
}: ApplyRotateStepOptions): void => {
  const increment = isFrontFace ? FRONT_FACE_ROTATE_INCREMENT : INSIDE_FACE_ROTATE_INCREMENT;
  const currentAngle = activeObject?.angle || 0;
  const nextAngle = rotateRight
    ? getComputedRotation(currentAngle + increment)
    : getComputedRotation(currentAngle - increment);

  rotateSelectedPhoto(Number(nextAngle));

  if (!activeObject || !isPhotozoneImage(activeObject) || !canvas) {
    return;
  }

  const photozoneId = activeObject.data?.photoZoneId;
  const photozoneObject = canvas.getObjects().find((object) => object.name === photozoneId);

  if (!photozoneObject) {
    return;
  }

  ensureImageFillsPhotoZone(activeObject, photozoneObject);
  const center = photozoneObject.getPointByOrigin('center', 'center');
  activeObject.setPositionByOrigin(center, 'center', 'center');
  activeObject.setCoords();
  canvas.renderAll();
};
